import { useState, type FC, useEffect, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { PhoneOutlined } from '@ant-design/icons';
import { Alert, Form, Popconfirm } from 'antd';
import { Button, Space } from '@g2u/ui-kit';
import ReactDOM from 'react-dom';

import {
  Intro,
  HomeAddressFields,
  BillingAddressFields,
  PrimaryContactFields,
  SecondaryContactFields,
  HearAboutUsFields,
  AccountNotesFields,
  Content,
} from 'components';
import { AccountType, Condition, type JsonDataItem } from 'types';
import { Actions, ConfirmPayment, Discount, Terms } from './components';
import { Toast, formatTimeToAmPm, formatToFullDate, parseErrors, validateFormSilently } from 'helpers';
import {
  getHearAboutUs,
  postCustomer,
  postCustomerDiscounts,
  postService,
  postVoipCall,
  putCustomerLink,
  putOtherDetails,
  putSearchHistory,
  putServiceSelection,
  putTaskClose,
} from 'queries';
import { BookingStore } from 'store';
import styles from './payment.module.scss';
import { useTaskCount } from 'hooks/useTaskCount';
import { format, parse } from 'date-fns';

export const BookingPaymentPage: FC = observer(() => {
  const [form] = Form.useForm();
  const {
    technician,
    isNewCustomer,
    location,
    customer,
    appointment,
    serviceSelection,
    setServiceSelection,
    slot,
    setIsNewCustomer,
    setStep,
    setCustomer,
    lostJob,
    productSelection,
    serviceType,
    setJob,
    callback,
    isAvailabilityOverrideEnabled,
  } = BookingStore;
  const navigate = useNavigate();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [newCustomerDiscounts, setNewCustomerDiscounts] = useState<JsonDataItem | null>();

  const { data: marketingOptions } = useQuery(['marketing-options'], getHearAboutUs, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: callTechnician, isLoading: isCalling } = useMutation(postVoipCall, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateSelection, isLoading: updatingSelection } = useMutation(putServiceSelection, {
    onSuccess: (data, variables) => {
      setServiceSelection(data.service_selection);
      newCustomerDiscounts
        ? addDiscounts({
            customerId: variables.customerId,
            discounts: newCustomerDiscounts.discounts.map((discount: JsonDataItem) => ({
              system_discount_id: discount.discountId[1], // Cascader returns an array
              approved_by: discount.approvedBy,
            })),
          })
        : createJob({
            appointmentId: appointment?.ID,
            serviceType,
            searchId: lostJob?.searchHistoryID,
            technicianNote: form.getFieldValue('techNotes'),
            billtoId: form.getFieldValue('thirdPartyId'),
            serviceSelectionId: serviceSelection?.ID,
            technicianId: technician?.ID,
            overrideTime: isAvailabilityOverrideEnabled
              ? slot?.day && `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`
              : undefined,
            callbackTaskId: callback?.ID,
          });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateSearchHistory } = useMutation(putSearchHistory, {
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateCustomerNotes, isLoading: updatingCustomer } = useMutation(putOtherDetails, {
    onSuccess: (data) => {
      setCustomer(data);
      Toast.show({
        description: `New changes on customer file #${data.ID} have been saved.`,
        type: Toast.Type.Success,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: createCustomer, isLoading: creatingCustomer } = useMutation(postCustomer, {
    onSuccess: (data) => {
      if (isNewCustomer && callback) {
        linkCustomer({ taskId: callback?.ID, customerId: data.ID });
      }
      updateSelection({
        selectionId: serviceSelection?.ID,
        customerId: data.ID,
        appointmentId: appointment?.ID,
        paymentMethod: form.getFieldValue('paymentMethod'),
      });
      setIsNewCustomer(false);
      setCustomer(data);
      datadogRum.setGlobalContextProperty('custid', String(data.ID));
      updateSearchHistory({
        searchId: lostJob?.searchHistoryID,
        customerId: data.ID,
      });
      Toast.show({
        description: `The new customer file #${data.ID} has been created. `,
        type: Toast.Type.Success,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: addDiscounts, isLoading: isDiscountsAdding } = useMutation(postCustomerDiscounts, {
    onSuccess: () =>
      createJob({
        appointmentId: appointment?.ID,
        serviceType,
        searchId: lostJob?.searchHistoryID,
        technicianNote: form.getFieldValue('techNotes'),
        billtoId: form.getFieldValue('thirdPartyId'),
        serviceSelectionId: serviceSelection?.ID,
        technicianId: technician?.ID,
        overrideTime: isAvailabilityOverrideEnabled
          ? slot?.day && `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`
          : undefined,
        callbackTaskId: callback?.ID,
      }),
    onError: (data) => Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error }),
  });

  const { mutate: createJob, isLoading: creatingJob } = useMutation(postService, {
    onSuccess: (data) => {
      setJob(data);
      callback?.ID &&
        closeCallback({
          taskId: callback?.ID,
          note: 'Booking confirmed - closing the callback.',
        });
      navigate(`/booking/confirmation/${data.ID}`);
      Toast.show({
        description: `Job #${data.ID} has been created.`,
        type: Toast.Type.Success,
      });
    },
    onError: (data) => {
      Toast.show({ description: `Error creating the job: ${parseErrors(data).join(', ')}`, type: Toast.Type.Error });
    },
  });

  const { refresh: refreshTaskCounter } = useTaskCount();

  const { mutate: closeCallback } = useMutation(putTaskClose, {
    onSuccess: (_data, payload) => {
      Toast.show({ description: `Callback ${payload.taskId} has been closed.`, type: Toast.Type.Success });
      refreshTaskCounter();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Link customer to callback if required
  const { mutate: linkCustomer } = useMutation(putCustomerLink, {
    onSuccess: (data, payload) => {
      Toast.show({
        description: (
          <>
            Customer file {payload.customerId} has been linked.
            <br />
            {data.data?.message}
          </>
        ),
        type: Toast.Type.Success,
      });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleFormFinish = (data: JsonDataItem) => {
    if (isNewCustomer) {
      createCustomer({
        type: productSelection?.accountType,
        pricingCategory: productSelection?.pricingCategory,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobileNumber: data.mobileNumber,
        phoneNumber: data.phoneNumber,
        secondaryContact: data.secondaryContact,
        secondaryMobileNumber: data.secondaryMobileNumber,
        secondaryFirstName: data.secondaryFirstName,
        secondaryLastName: data.secondaryLastName,
        abn: data.abn === '' ? undefined : data.abn,
        companyName: data.companyName,
        homeAddressLine1: data.homeAddressLine1,
        homeAddressLine2: data.homeAddressLine2,
        homeAddressSuburb: data.homeAddressSuburb,
        homeAddressState: data.homeAddressState,
        homeAddressPostcode: data.homeAddressPostcode,
        sameBillingAddress: data.sameBillingAddress ? Condition.Yes : Condition.No,
        postalAddressLine1: data.postalAddressLine1,
        postalAddressLine2: data.postalAddressLine2,
        postalAddressSuburb: data.postalAddressSuburb,
        postalAddressState: data.postalAddressState,
        postalAddressPostcode: data.postalAddressPostcode,
        accessNotes: data.accessNotes,
        hearAboutUs: data.hearAboutUs,
        searchId: lostJob?.searchHistoryID,
        marketing: 'Y', // as requested
      });
    } else {
      form.getFieldValue('accessNotes') !== customer?.access_notes &&
        updateCustomerNotes({
          id: customer?.ID,
          accessNotes: data.accessNotes,
        });
      updateSelection({
        selectionId: serviceSelection?.ID,
        customerId: customer?.ID,
        appointmentId: appointment?.ID,
        paymentMethod: data.paymentMethod,
      });
    }
  };

  const handleFormChange = () => {
    // enable "next" button
    void validateFormSilently(form, setIsFormValid);
  };

  useEffect(() => {
    setStep(3);
    void validateFormSilently(form, setIsFormValid);
  }, []);

  const portalElement = document.getElementById('booking-progress-portal');

  return (
    <Content className={styles.payment} dataTestId="payment-page">
      <title>Customer details and payment method</title>
      {technician &&
        portalElement &&
        (ReactDOM.createPortal(
          <Alert
            message={
              <>
                Technician <b>{technician?.name.split(' ')[0]}</b> and arrival window{' '}
                <b>
                  {formatTimeToAmPm(slot?.time)}-{formatTimeToAmPm(slot?.time, 1)} - {formatToFullDate(slot?.day)}
                </b>{' '}
                have been selected.
              </>
            }
            type="info"
            showIcon
            closable
            className=""
            action={
              <>
                <Popconfirm
                  title="Alert"
                  onConfirm={() => {
                    callTechnician(technician?.contact[0].replace(/\s/g, ''));
                  }}
                  description={`Do you want to call ${technician?.name.split(' ')[0]}`}
                  okText="Yes"
                >
                  <div>
                    <Button.$ size={Button.Size.Small} loading={isCalling} onClick={() => null} dataTestId="call">
                      <PhoneOutlined /> Call
                    </Button.$>
                  </div>
                </Popconfirm>
              </>
            }
          />,
          portalElement,
        ) as ReactNode)}
      <Form form={form} colon={false} layout="horizontal" onValuesChange={handleFormChange} onFinish={handleFormFinish}>
        <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
          <Intro />

          {/* <AccountDetailsFields /> */}

          {isNewCustomer && (
            <>
              <PrimaryContactFields
                form={form}
                customer={null}
                hasScript
                isBusinessAccount={productSelection?.accountType === AccountType.Company}
                initialValues={{
                  firstName: callback?.record.name,
                  lastName: callback?.record.lname,
                  mobile: callback?.record.phone,
                  email: callback?.record.email,
                }}
              />
              <SecondaryContactFields customer={null} hasScript />
              <HomeAddressFields form={form} customer={null} location={location} />
              <BillingAddressFields form={form} customer={null} />
            </>
          )}

          <AccountNotesFields customer={customer} hasTechNote />
          {isNewCustomer && <HearAboutUsFields options={marketingOptions?.data} hasScript required />}
          <Discount
            customerId={customer?.ID}
            onSaveNewCustomerDicounts={setNewCustomerDiscounts}
            newCustomerDiscounts={newCustomerDiscounts}
          />
          <ConfirmPayment />
          <Terms />
          <Actions
            active={isFormValid}
            isLoading={creatingCustomer || updatingSelection || creatingJob || updatingCustomer || isDiscountsAdding}
            onBack={() => {
              navigate('/booking/technician');
            }}
            onNext={() => {
              form.submit();
            }}
            hasSlot={Boolean(slot)}
          />
        </Space.$>
      </Form>
    </Content>
  );
});
